import React, { useState, useEffect, useRef, useCallback } from 'react';
import { get, post } from '../../services/smartApiService';
import { OPTIONS_URLS } from '../../api/OptionsUrls';
import { kiteWebSocket, TickData, InstrumentToken } from '../../services/kiteWebSocketService';
import { DetectedPattern, PATTERN_LABEL, PATTERN_FULL_NAME, PATTERN_EXPLANATION } from '../../utils/chartPatternDetector';
import AdvancedTradingChart from './components/AdvancedTradingChart';
import OIChart from './components/OIChart';
import StrategySignalPanel from './components/StrategySignalPanel';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SpotData {
  symbol: string;
  spot_price: number;
  open: number;
  high: number;
  low: number;
  close: number;
  change: number;
  change_percent: number;
  volume: number;
}

interface OptionRow {
  strike: number;
  callOI: number;
  callVolume: number;
  callLTP: number;
  callIV: string;
  callBid: number;
  callAsk: number;
  putOI: number;
  putVolume: number;
  putLTP: number;
  putIV: string;
  putBid: number;
  putAsk: number;
}

interface OILevel {
  level: number;
  type: 'oi_support' | 'oi_resistance' | 'max_pain';
  label: string;
  oi?: number;
}

interface SRLevel {
  level: number;
  type: string;
  strength: string;
  reason: string;
}

interface OIChangeRow {
  strike: number;
  callOIChange: number;
  putOIChange: number;
}

interface IndexState {
  spotData: SpotData | null;
  livePrice: number;
  optionChain: OptionRow[];
  techIndicators: any;
  pcr: number;
  maxPain: number;
  oiLevels: OILevel[];
  supportLevels: SRLevel[];
  resistanceLevels: SRLevel[];
  pcrTrend: { time: string; pcr: number }[];
  oiChangeData: OIChangeRow[];
  oiChangeTotals: { callChange: number; putChange: number } | null;
  loading: boolean;
  lastUpdated: Date | null;
}

interface SignalMarker {
  type: 'CE' | 'PE';
  score: number;
  timestamp: number;
  symbol: string;
  date: string;
}

type CandleStatus = 'checking' | 'ok' | 'fetching' | 'done' | 'error';

// Fetching 5m is sufficient — the backend cascade auto-aggregates 5m → 15m → 1h → 1d etc.
const FETCH_TIMEFRAMES = ['5m'];

const INDEX_CONFIGS = [
  { symbol: 'NIFTY', label: 'NIFTY 50', color: '#4fc3f7', exchange: 'NSE' },
  { symbol: 'SENSEX', label: 'SENSEX', color: '#26a69a', exchange: 'BSE' },
] as const;

// SENSEX trades on BSE, not NSE — used when calling fetch-historical
const EXCHANGE_BY_SYMBOL: Record<string, string> = Object.fromEntries(
  INDEX_CONFIGS.map(({ symbol, exchange }) => [symbol, exchange])
);

// ─── Signal Marker localStorage ───────────────────────────────────────────────

const MARKERS_KEY = 'mktpulse_signals';
const todayStr = () => new Date().toISOString().slice(0, 10);

const loadMarkersFromStorage = (): Record<string, SignalMarker[]> => {
  try {
    const raw = localStorage.getItem(MARKERS_KEY);
    if (!raw) return {};
    const all: Record<string, SignalMarker[]> = JSON.parse(raw);
    const today = todayStr();
    const pruned: Record<string, SignalMarker[]> = {};
    for (const [sym, markers] of Object.entries(all)) {
      const fresh = markers.filter(m => m.date === today);
      if (fresh.length > 0) pruned[sym] = fresh;
    }
    return pruned;
  } catch { return {}; }
};

const saveMarkersToStorage = (markers: Record<string, SignalMarker[]>) => {
  try { localStorage.setItem(MARKERS_KEY, JSON.stringify(markers)); } catch { /* quota */ }
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isMarketOpen = (): boolean => {
  const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const day = ist.getDay();
  if (day === 0 || day === 6) return false;
  const mins = ist.getHours() * 60 + ist.getMinutes();
  return mins >= 555 && mins <= 930;
};

// ─── Session Phase & Expiry Helpers ──────────────────────────────────────────

const getISTNow = () =>
  new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));

const getSessionPhase = () => {
  const ist = getISTNow();
  const day = ist.getDay();
  if (day === 0 || day === 6) return { phase: 'Weekend', color: '#555', minsLeft: 0, next: '' };
  const m = ist.getHours() * 60 + ist.getMinutes();
  if (m < 540) return { phase: 'Pre-Market', color: '#9e9e9e', minsLeft: 540 - m, next: 'Pre-open 9:00' };
  if (m < 555) return { phase: 'Pre-open', color: '#ffd54f', minsLeft: 555 - m, next: 'Opening 9:15' };
  if (m < 585) return { phase: 'Opening', color: '#ff9800', minsLeft: 585 - m, next: 'Mid-Session 9:45' };
  if (m < 720) return { phase: 'Mid-Session', color: '#4fc3f7', minsLeft: 720 - m, next: 'Post-Noon 12:00' };
  if (m < 840) return { phase: 'Post-Noon', color: '#9c27b0', minsLeft: 840 - m, next: 'Power Hour 14:00' };
  if (m < 900) return { phase: '⚡ Power Hour', color: '#ef5350', minsLeft: 900 - m, next: 'Closing 15:00' };
  if (m < 930) return { phase: 'Closing', color: '#ffd54f', minsLeft: 930 - m, next: 'Closed 15:30' };
  return { phase: 'Closed', color: '#555', minsLeft: 0, next: '' };
};

const getExpiryInfo = () => {
  const ist = getISTNow();
  const day = ist.getDay();
  const weekly = day <= 4 ? 4 - day : 7 - (day - 4);
  const yr = ist.getFullYear(), mo = ist.getMonth();
  const lastDay = new Date(yr, mo + 1, 0).getDate();
  const lastThu = lastDay - ((new Date(yr, mo, lastDay).getDay() - 4 + 7) % 7);
  let monthlyMs = new Date(yr, mo, lastThu, 15, 30).getTime() - ist.getTime();
  if (monthlyMs < 0) {
    const nm = mo === 11 ? 0 : mo + 1, ny = mo === 11 ? yr + 1 : yr;
    const nld = new Date(ny, nm + 1, 0).getDate();
    const nlt = nld - ((new Date(ny, nm, nld).getDay() - 4 + 7) % 7);
    monthlyMs = new Date(ny, nm, nlt, 15, 30).getTime() - ist.getTime();
  }
  return { weekly, monthly: Math.max(0, Math.ceil(monthlyMs / 86400000)) };
};

const fmt = (n: number, dec = 2) =>
  n?.toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec }) ?? '—';

const fmtOI = (v: number) => {
  if (v >= 1e7) return (v / 1e7).toFixed(1) + 'Cr';
  if (v >= 1e5) return (v / 1e5).toFixed(1) + 'L';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return v.toString();
};

const computeMaxPain = (chain: OptionRow[]): number => {
  if (!chain.length) return 0;
  let minPain = Infinity, best = 0;
  for (const t of chain) {
    let pain = 0;
    for (const r of chain) {
      if (t.strike > r.strike) pain += r.callOI * (t.strike - r.strike);
      if (t.strike < r.strike) pain += r.putOI * (r.strike - t.strike);
    }
    if (pain < minPain) { minPain = pain; best = t.strike; }
  }
  return best;
};

const computeOILevels = (chain: OptionRow[], _spot: number): OILevel[] => {
  if (!chain.length) return [];
  const byCE = [...chain].sort((a, b) => b.callOI - a.callOI).slice(0, 3);
  const byPE = [...chain].sort((a, b) => b.putOI - a.putOI).slice(0, 3);
  const maxPain = computeMaxPain(chain);
  return [
    ...byCE.map(r => ({ level: r.strike, type: 'oi_resistance' as const, label: `CE Wall ${fmtOI(r.callOI)}`, oi: r.callOI })),
    ...byPE.map(r => ({ level: r.strike, type: 'oi_support' as const, label: `PE Wall ${fmtOI(r.putOI)}`, oi: r.putOI })),
    { level: maxPain, type: 'max_pain' as const, label: `Max Pain ${maxPain}` },
  ];
};

const computeTechLevels = (tech: any): { support: SRLevel[]; resistance: SRLevel[] } => {
  if (!tech?.pivot_points) return { support: [], resistance: [] };
  const pp = tech.pivot_points;
  const mas = tech.moving_averages;
  const bb = tech.bollinger_bands;
  const spot = tech.spot_price || 0;
  const support: SRLevel[] = [];
  const resistance: SRLevel[] = [];

  const addLevel = (level: number | null | undefined, type: string, strength: string, reason: string) => {
    if (!level || level <= 0) return;
    if (level < spot) support.push({ level, type, strength, reason });
    else resistance.push({ level, type, strength, reason });
  };

  addLevel(pp.pivot, 'pivot', 'strong', 'Daily Pivot');
  addLevel(pp.r1, 'resistance', 'strong', 'Pivot R1');
  addLevel(pp.r2, 'resistance', 'strong', 'Pivot R2');
  addLevel(pp.r3, 'resistance', 'strong', 'Pivot R3');
  addLevel(pp.s1, 'support', 'strong', 'Pivot S1');
  addLevel(pp.s2, 'support', 'strong', 'Pivot S2');
  addLevel(pp.s3, 'support', 'strong', 'Pivot S3');
  addLevel(mas?.sma_20, 'sma', 'medium', 'SMA 20');
  addLevel(mas?.sma_50, 'sma', 'strong', 'SMA 50');
  addLevel(mas?.sma_200, 'sma', 'strong', 'SMA 200');
  addLevel(bb?.upper, 'bb', 'medium', 'BB Upper');
  addLevel(bb?.lower, 'bb', 'medium', 'BB Lower');

  return { support, resistance };
};

const makeDefaultState = (): IndexState => ({
  spotData: null, livePrice: 0, optionChain: [], techIndicators: null,
  pcr: 0, maxPain: 0, oiLevels: [], supportLevels: [], resistanceLevels: [],
  pcrTrend: [], oiChangeData: [], oiChangeTotals: null, loading: false, lastUpdated: null,
});

// ─── Mini Tech Panel ──────────────────────────────────────────────────────────

const MiniTechPanel: React.FC<{ tech: any; pcr: number; maxPain: number }> = ({ tech, pcr, maxPain }) => {
  if (!tech) {
    return (
      <div style={{ padding: '0.5rem', fontSize: '0.68rem', color: '#555', background: '#131722', borderTop: '1px solid #1e222d' }}>
        Indicators loading…
      </div>
    );
  }
  const rsi = tech.rsi?.value;
  const rsiColor = rsi > 70 ? '#ef5350' : rsi < 30 ? '#26a69a' : '#ffd54f';
  const macdSig = tech.macd?.signal || 'neutral';
  const adx = tech.adx?.value;
  const adxSig = tech.adx?.signal || '';
  const overall = tech.summary?.overall_signal || 'neutral';
  const sigColor = overall.includes('buy') ? '#26a69a' : overall.includes('sell') ? '#ef5350' : '#ffd54f';
  const pcrColor = pcr > 1.2 ? '#26a69a' : pcr < 0.7 ? '#ef5350' : '#ffd54f';

  const chip = (label: string, value: string | number, color: string) => (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', background: '#1e222d', borderRadius: 4, padding: '3px 8px', minWidth: 52 }}>
      <span style={{ fontSize: '0.5rem', color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      <span style={{ fontSize: '0.72rem', fontWeight: 700, color, fontFamily: 'monospace' }}>{value}</span>
    </div>
  );

  return (
    <div style={{ padding: '0.4rem 0.5rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', background: '#131722', borderTop: '1px solid #1e222d' }}>
      {chip('RSI', rsi ? rsi.toFixed(1) : '—', rsiColor)}
      {chip('MACD', macdSig === 'bullish' ? '▲ Bull' : macdSig === 'bearish' ? '▼ Bear' : '— Neut', macdSig === 'bullish' ? '#26a69a' : macdSig === 'bearish' ? '#ef5350' : '#ffd54f')}
      {chip('ADX', adx ? adx.toFixed(1) : '—', adxSig === 'strong_trend' ? '#ffd54f' : '#555')}
      {chip('PCR', pcr ? pcr.toFixed(2) : '—', pcrColor)}
      {chip('MaxPain', maxPain ? maxPain.toLocaleString('en-IN') : '—', '#9e9e9e')}
      {chip('Signal', overall.replace('_', ' ').toUpperCase(), sigColor)}
      <div style={{ marginLeft: 'auto', fontSize: '0.6rem', color: '#444' }}>
        {tech.calculated_at ? new Date(tech.calculated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
      </div>
    </div>
  );
};

// ─── Candle Status Overlay ────────────────────────────────────────────────────

const CandleStatusOverlay: React.FC<{
  status: CandleStatus;
  fetchProgress: string;
  onManualFetch: () => void;
}> = ({ status, fetchProgress, onManualFetch }) => {
  if (status === 'ok' || status === 'done') return null;

  const bgColor = 'rgba(13,13,20,0.92)';

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: bgColor, gap: '0.75rem' }}>
      {status === 'checking' && (
        <>
          <div style={{ width: 28, height: 28, border: '3px solid #2b2b43', borderTopColor: '#4fc3f7', borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
          <span style={{ color: '#787b86', fontSize: '0.75rem' }}>Checking candle data…</span>
        </>
      )}
      {status === 'fetching' && (
        <>
          <div style={{ width: 28, height: 28, border: '3px solid #2b2b43', borderTopColor: '#26a69a', borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
          <span style={{ color: '#26a69a', fontSize: '0.78rem', fontWeight: 600 }}>Fetching candles from Kite…</span>
          <span style={{ color: '#555', fontSize: '0.68rem' }}>{fetchProgress}</span>
          <span style={{ color: '#444', fontSize: '0.62rem' }}>This runs once — data will be saved for future use</span>
        </>
      )}
      {status === 'error' && (
        <>
          <span style={{ color: '#ef5350', fontSize: '0.78rem' }}>Could not auto-fetch candles</span>
          <button onClick={onManualFetch} style={{ padding: '5px 14px', fontSize: '0.68rem', background: '#1e222d', border: '1px solid #363a45', color: '#d1d4dc', borderRadius: 3, cursor: 'pointer' }}>
            Retry Fetch
          </button>
        </>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─── Session Expiry Bar ───────────────────────────────────────────────────────

const SessionExpiryBar: React.FC = () => {
  const [phase, setPhase] = useState(() => getSessionPhase());
  const [expiry, setExpiry] = useState(() => getExpiryInfo());
  useEffect(() => {
    const t = setInterval(() => { setPhase(getSessionPhase()); setExpiry(getExpiryInfo()); }, 60000);
    return () => clearInterval(t);
  }, []);
  const { weekly, monthly } = expiry;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.28rem 0.6rem', background: '#0d0d14', border: '1px solid #1e222d', borderRadius: 5, fontSize: '0.63rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: phase.color, display: 'inline-block', flexShrink: 0 }} />
      <span style={{ color: phase.color, fontWeight: 700 }}>{phase.phase}</span>
      {phase.minsLeft > 0 && <span style={{ color: '#555' }}>· {phase.minsLeft}m → {phase.next}</span>}
      <span style={{ color: '#2b2b43' }}>│</span>
      <span style={{ color: '#787b86' }}>Expiry:</span>
      <span>Weekly <span style={{ color: weekly === 0 ? '#ef5350' : weekly <= 1 ? '#ffd54f' : '#d1d4dc', fontWeight: 700 }}>{weekly === 0 ? 'Today!' : `${weekly}d`}</span></span>
      <span>Monthly <span style={{ color: monthly <= 3 ? '#ffd54f' : '#9e9e9e', fontWeight: 700 }}>{monthly}d</span></span>
    </div>
  );
};

// ─── Levels Modal ─────────────────────────────────────────────────────────────

const LevelsModal: React.FC<{
  config: typeof INDEX_CONFIGS[number];
  state: IndexState;
  onClose: () => void;
}> = ({ config, state, onClose }) => {
  const { oiLevels, supportLevels, resistanceLevels, techIndicators, pcr, maxPain } = state;
  const pp = techIndicators?.pivot_points;
  const mas = techIndicators?.moving_averages;
  const bb = techIndicators?.bollinger_bands;

  const row = (label: string, value: string | number | null | undefined, color = '#d1d4dc') =>
    value != null && value !== 0 ? (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #1a1e2a' }}>
        <span style={{ color: '#787b86', fontSize: '0.68rem' }}>{label}</span>
        <span style={{ color, fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 600 }}>{typeof value === 'number' ? value.toLocaleString('en-IN') : value}</span>
      </div>
    ) : null;

  const ceWalls = oiLevels.filter(l => l.type === 'oi_resistance');
  const peWalls = oiLevels.filter(l => l.type === 'oi_support');
  const mp = oiLevels.find(l => l.type === 'max_pain');

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#131722', border: '1px solid #2b2b43', borderRadius: 8, width: 420, maxHeight: '85vh', overflow: 'auto', padding: '1rem' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
          <span style={{ color: config.color, fontWeight: 800, fontSize: '0.9rem', letterSpacing: 0.5 }}>{config.label}</span>
          <span style={{ color: '#555', fontSize: '0.7rem', marginLeft: '0.5rem' }}>— Levels Detail</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#787b86', fontSize: '1rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        {/* OI Levels */}
        {(ceWalls.length > 0 || peWalls.length > 0 || mp) && (
          <section style={{ marginBottom: '0.75rem' }}>
            <div style={{ color: '#787b86', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: '0.35rem' }}>OI Levels</div>
            {ceWalls.map((l, i) => row(`CE Wall ${i + 1}${l.oi ? ` (${fmtOI(l.oi)})` : ''}`, l.level, '#ef5350'))}
            {peWalls.map((l, i) => row(`PE Wall ${i + 1}${l.oi ? ` (${fmtOI(l.oi)})` : ''}`, l.level, '#26a69a'))}
            {mp && row('Max Pain', mp.level, '#ffd54f')}
            {row('PCR', pcr ? pcr.toFixed(2) : null, pcr > 1.2 ? '#26a69a' : pcr < 0.7 ? '#ef5350' : '#ffd54f')}
          </section>
        )}

        {/* Pivot Points */}
        {pp && (
          <section style={{ marginBottom: '0.75rem' }}>
            <div style={{ color: '#787b86', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: '0.35rem' }}>Pivot Points</div>
            {row('R3', pp.r3, '#ef5350')}
            {row('R2', pp.r2, '#ef5350')}
            {row('R1', pp.r1, '#ef5350')}
            {row('Pivot', pp.pivot, '#4fc3f7')}
            {row('S1', pp.s1, '#26a69a')}
            {row('S2', pp.s2, '#26a69a')}
            {row('S3', pp.s3, '#26a69a')}
          </section>
        )}

        {/* Moving Averages */}
        {mas && (
          <section style={{ marginBottom: '0.75rem' }}>
            <div style={{ color: '#787b86', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: '0.35rem' }}>Moving Averages</div>
            {row('EMA 9', mas.ema_9, '#FF9800')}
            {row('EMA 21', mas.ema_21, '#FF9800')}
            {row('SMA 20', mas.sma_20, '#FF9800')}
            {row('SMA 50', mas.sma_50, '#FF9800')}
            {row('SMA 200', mas.sma_200, '#FF9800')}
          </section>
        )}

        {/* Bollinger Bands */}
        {bb && (
          <section style={{ marginBottom: '0.75rem' }}>
            <div style={{ color: '#787b86', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: '0.35rem' }}>Bollinger Bands</div>
            {row('Upper', bb.upper, '#9c27b0')}
            {row('Middle', bb.middle, '#9c27b0')}
            {row('Lower', bb.lower, '#9c27b0')}
          </section>
        )}

        {/* S&R summary */}
        {(supportLevels.length > 0 || resistanceLevels.length > 0) && (
          <section>
            <div style={{ color: '#787b86', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: '0.35rem' }}>S&amp;R from Indicators</div>
            {resistanceLevels.slice(0, 5).map((l, i) => row(`R${i + 1} · ${l.reason}`, l.level, '#ef5350'))}
            {supportLevels.slice(0, 5).map((l, i) => row(`S${i + 1} · ${l.reason}`, l.level, '#26a69a'))}
          </section>
        )}

        <div style={{ marginTop: '0.75rem', fontSize: '0.58rem', color: '#444', textAlign: 'right' }}>
          {techIndicators?.calculated_at ? `Updated: ${new Date(techIndicators.calculated_at).toLocaleTimeString('en-IN')}` : ''}
        </div>
      </div>
    </div>
  );
};

// ─── Pattern Insights Panel ──────────────────────────────────────────────────
// Explains, in plain English, what a detected chart pattern typically implies for
// price — shown under the chart since lightweight-charts markers only support a
// short text label, not a hoverable tooltip with real explanation text.

const PatternInsightsPanel: React.FC<{ patterns: DetectedPattern[] }> = ({ patterns }) => {
  if (!patterns.length) return null;
  return (
    <div style={{ padding: '0.5rem 0.75rem', background: '#0d0d14', borderTop: '1px solid #1e222d', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      {patterns.map(p => {
        const color = p.signal === 'bull' ? '#4fc3f7' : '#FF9800';
        return (
          <div key={p.type} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', fontSize: '0.68rem', lineHeight: 1.4 }}>
            <span style={{ flexShrink: 0, fontWeight: 800, color, border: `1px solid ${color}`, borderRadius: 3, padding: '1px 5px', fontSize: '0.62rem' }}>
              {PATTERN_LABEL[p.type]}{p.confirmed ? ' ✓' : ' ?'}
            </span>
            <span style={{ color: '#787b86' }}>
              <span style={{ color: '#d1d4dc', fontWeight: 600 }}>{PATTERN_FULL_NAME[p.type]}</span>
              {' '}· {p.confidence}% confidence · {p.confirmed ? 'confirmed' : 'watching, not yet confirmed'}
              <br />
              {PATTERN_EXPLANATION[p.type]}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Index Panel ─────────────────────────────────────────────────────────────

interface IndexPanelProps {
  config: typeof INDEX_CONFIGS[number];
  state: IndexState;
  wsEnabled: boolean;
  candleStatus: CandleStatus;
  fetchProgress: string;
  chartKey: number;
  onManualFetch: () => void;
  onShowLevels: () => void;
  signalMarkers: SignalMarker[];
  onSignalChange: (signal: { type: 'CE' | 'PE'; score: number } | null) => void;
  chartPatterns: DetectedPattern[];
  onPatternsDetected: (patterns: DetectedPattern[]) => void;
}

const IndexPanel: React.FC<IndexPanelProps> = ({ config, state, wsEnabled, candleStatus, fetchProgress, chartKey, onManualFetch, onShowLevels, signalMarkers, onSignalChange, chartPatterns, onPatternsDetected }) => {
  const { spotData, livePrice, optionChain, techIndicators, pcr, maxPain, oiLevels, supportLevels, resistanceLevels, pcrTrend, oiChangeData, oiChangeTotals } = state;
  const spot = livePrice || spotData?.spot_price || 0;
  const chg = spotData ? spot - spotData.close : 0;
  const chgPct = spotData?.close ? (chg / spotData.close) * 100 : 0;
  const isUp = chg >= 0;

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#0d0d14', border: '1px solid #1e222d', borderRadius: 6, overflow: 'hidden' }}>
      {/* Price Header */}
      <div style={{ padding: '0.5rem 0.75rem', background: '#131722', borderBottom: '1px solid #1e222d', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <span style={{ color: config.color, fontWeight: 800, fontSize: '0.85rem', letterSpacing: 0.5 }}>{config.label}</span>
        <span style={{ color: '#d1d4dc', fontWeight: 700, fontSize: '1.1rem', fontFamily: 'monospace' }}>
          {spot > 0 ? fmt(spot, 2) : '—'}
        </span>
        {spot > 0 && (
          <span style={{ color: isUp ? '#26a69a' : '#ef5350', fontSize: '0.78rem', fontWeight: 600 }}>
            {isUp ? '▲' : '▼'} {fmt(Math.abs(chg))} ({fmt(Math.abs(chgPct))}%)
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', fontSize: '0.6rem', color: '#555', alignItems: 'center', flexWrap: 'wrap' }}>
          {spotData && (
            <>
              <span>O:<span style={{ color: '#d1d4dc' }}>{fmt(spotData.open)}</span></span>
              <span>H:<span style={{ color: '#26a69a' }}>{fmt(spotData.high)}</span></span>
              <span>L:<span style={{ color: '#ef5350' }}>{fmt(spotData.low)}</span></span>
              {(() => {
                const range = spotData.high - spotData.low;
                const atr = techIndicators?.atr?.value;
                if (!atr || atr <= 0 || range <= 0) return null;
                const util = Math.round((range / atr) * 100);
                const utilColor = util > 80 ? '#ef5350' : util > 60 ? '#ffd54f' : '#26a69a';
                return (
                  <span style={{ marginLeft: '0.2rem' }}>
                    Rng <span style={{ color: '#9e9e9e' }}>{Math.round(range)}pt</span>
                    {' '}·ATR <span style={{ color: '#787b86' }}>{Math.round(atr)}pt</span>
                    {' '}·<span style={{ color: utilColor, fontWeight: 700 }}>{util}%</span>
                  </span>
                );
              })()}
            </>
          )}
        </div>
      </div>

      {/* Chart + overlay */}
      <div style={{ position: 'relative' }}>
        <CandleStatusOverlay
          status={candleStatus}
          fetchProgress={fetchProgress}
          onManualFetch={onManualFetch}
        />
        <AdvancedTradingChart
          key={chartKey}
          symbol={config.symbol}
          height={440}
          wsEnabled={wsEnabled}
          livePrice={livePrice}
          supportLevels={supportLevels}
          resistanceLevels={resistanceLevels}
          oiLevels={oiLevels}
          showVWAP={true}
          showVolume={false}
          signalMarkers={signalMarkers}
          onPatternsDetected={onPatternsDetected}
        />
      </div>

      {/* Pattern insights — what each detected pattern typically means for price */}
      <PatternInsightsPanel patterns={chartPatterns} />

      {/* Levels detail button */}
      <div style={{ padding: '0.2rem 0.6rem', background: '#0d1117', borderTop: '1px solid #1e222d', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onShowLevels}
          style={{ background: 'none', border: 'none', fontSize: '0.6rem', color: '#2962ff', cursor: 'pointer', padding: 0, letterSpacing: 0.3 }}
        >
          OI · S&amp;R · Pivot · MA levels →
        </button>
      </div>

      {/* Mini Tech Panel */}
      <MiniTechPanel tech={techIndicators} pcr={pcr} maxPain={maxPain} />

      {/* Strategy Signal Panel */}
      <StrategySignalPanel
        symbol={config.symbol}
        spotPrice={spot}
        tech={techIndicators}
        optionChain={optionChain}
        pcr={pcr}
        maxPain={maxPain}
        oiLevels={oiLevels}
        onSignalChange={onSignalChange}
        chartPatterns={chartPatterns}
        oiChangeTotals={oiChangeTotals}
      />

      {/* OI Chart */}
      {optionChain.length > 0 ? (
        <div style={{ padding: '0.25rem', borderTop: '1px solid #1e222d' }}>
          <OIChart optionChain={optionChain} spotPrice={spot} height={220} pcrTrend={pcrTrend} oiChangeData={oiChangeData} />
        </div>
      ) : (
        <div style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.68rem', color: '#555', borderTop: '1px solid #1e222d' }}>
          Option chain available during market hours only
        </div>
      )}
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const MarketPulseDashboard: React.FC = () => {
  const [indexStates, setIndexStates] = useState<Record<string, IndexState>>({
    NIFTY: makeDefaultState(),
    SENSEX: makeDefaultState(),
  });

  // Candle data tracking per symbol
  const [candleStatus, setCandleStatus] = useState<Record<string, CandleStatus>>({
    NIFTY: 'checking',
    SENSEX: 'checking',
  });
  const [fetchProgress, setFetchProgress] = useState<Record<string, string>>({
    NIFTY: '',
    SENSEX: '',
  });
  // Increment to force AdvancedTradingChart to remount after candles are fetched
  const [chartKeys, setChartKeys] = useState<Record<string, number>>({ NIFTY: 0, SENSEX: 0 });

  const [levelsModalSymbol, setLevelsModalSymbol] = useState<string | null>(null);
  const [signalMarkers, setSignalMarkers] = useState<Record<string, SignalMarker[]>>(() => loadMarkersFromStorage());
  const [vixData, setVixData] = useState<{ value: number } | null>(null);
  const [chartPatterns, setChartPatterns] = useState<Record<string, DetectedPattern[]>>({
    NIFTY: [], SENSEX: [],
  });

  const handlePatternsDetected = useCallback((symbol: string, patterns: DetectedPattern[]) => {
    setChartPatterns(prev => ({ ...prev, [symbol]: patterns }));
  }, []);

  const handleSignalChange = useCallback((symbol: string, signal: { type: 'CE' | 'PE'; score: number } | null) => {
    if (!signal) return; // threshold dropped — keep existing markers
    const marker: SignalMarker = {
      type: signal.type,
      score: signal.score,
      timestamp: Date.now(),
      symbol,
      date: todayStr(),
    };
    setSignalMarkers(prev => {
      const updated = { ...prev, [symbol]: [...(prev[symbol] ?? []), marker] };
      saveMarkersToStorage(updated);
      return updated;
    });
  }, []);

  const [wsEnabled, setWsEnabled] = useState(false);
  const [wsStatus, setWsStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  const [wsMessage, setWsMessage] = useState('');
  const [globalLoading, setGlobalLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [nextRefreshIn, setNextRefreshIn] = useState(300);

  const loadVix = useCallback(async () => {
    try {
      const res: any = await get('/api/options/vix', { showLoading: false }).toPromise();
      if (res?.data?.value != null) setVixData({ value: res.data.value });
    } catch { /* VIX is optional */ }
  }, []);

  const spotTokenMapRef = useRef<Map<number, string>>(new Map());
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const wsEnabledRef = useRef(false);

  const updateIndex = useCallback((symbol: string, patch: Partial<IndexState>) => {
    setIndexStates(prev => ({ ...prev, [symbol]: { ...prev[symbol], ...patch } }));
  }, []);

  useEffect(() => { wsEnabledRef.current = wsEnabled; }, [wsEnabled]);

  // ── Check if candle data exists and is recent; if not, auto-fetch from Kite ──
  const checkAndFetchCandles = useCallback(async (symbol: string): Promise<boolean> => {
    setCandleStatus(p => ({ ...p, [symbol]: 'checking' }));
    try {
      // Quick probe: ask for 3 daily candles
      const probe: any = await get(
        `/api/dashboard/prices/symbol/${symbol}/history?timeframe=1day&limit=3`,
        { showLoading: false }
      ).toPromise();

      const candles: any[] = probe?.data?.data?.candles ?? [];
      if (candles.length > 0) {
        const latestTs = candles.reduce((best, c) => {
          const t = new Date(c.timestamp ?? c.time ?? 0).getTime();
          return t > best ? t : best;
        }, 0);
        const IST_OFFSET_MS = 5.5 * 3600 * 1000;
        const nowIstDays = Math.floor((Date.now() + IST_OFFSET_MS) / 86400000);
        const latestIstDays = Math.floor((latestTs + IST_OFFSET_MS) / 86400000);
        const daysDiff = nowIstDays - latestIstDays;
        // Max weekend gap is 3 days (Mon - Fri); allow up to 4 to cover public holidays
        if (daysDiff <= 4) {
          setCandleStatus(p => ({ ...p, [symbol]: 'ok' }));
          return false;
        }
        console.log(`[MarketPulse] ${symbol}: daily candles are ${daysDiff} days stale — re-fetching`);
        // Fall through to re-fetch
      }

      // No data, or data is stale — fetch all timeframes in parallel
      setCandleStatus(p => ({ ...p, [symbol]: 'fetching' }));
      setFetchProgress(p => ({ ...p, [symbol]: 'Fetching candles…' }));

      await Promise.all(FETCH_TIMEFRAMES.map(tf => {
        const params = new URLSearchParams({ symbol, exchange: EXCHANGE_BY_SYMBOL[symbol] ?? 'NSE', timeframe: tf });
        return post(`/api/dashboard/prices/fetch-historical?${params.toString()}`, {}, { showLoading: false })
          .toPromise()
          .catch((e: any) => console.warn(`[MarketPulse] fetch-historical ${tf} for ${symbol} failed:`, e));
      }));

      setFetchProgress(p => ({ ...p, [symbol]: '' }));
      setCandleStatus(p => ({ ...p, [symbol]: 'done' }));
      setChartKeys(p => ({ ...p, [symbol]: p[symbol] + 1 }));
      return true;
    } catch (e) {
      console.error(`[MarketPulse] candle check failed for ${symbol}:`, e);
      setCandleStatus(p => ({ ...p, [symbol]: 'error' }));
      return false;
    }
  }, []);

  // ── Fill recent gap if latest intraday candle is stale — either from a prior trading
  // day, or (during market hours) more than 10 minutes old even within today ──
  const fillRecentGap = useCallback(async (symbol: string): Promise<boolean> => {
    try {
      // NOTE: GET history endpoint uses 'min/hour/day' suffixes (5min, 1hour, 1day),
      // while POST fetch-historical uses short form (5m, 1h, 1d). These are different.
      const res: any = await get(
        `/api/dashboard/prices/symbol/${symbol}/history?timeframe=5min&limit=1`,
        { showLoading: false }
      ).toPromise();

      const candles: any[] = res?.data?.data?.candles ?? [];
      if (!candles.length) return false;

      const lastTs = candles.reduce((best, c) => {
        const t = new Date(c.timestamp ?? c.time ?? 0).getTime();
        return t > best ? t : best;
      }, 0);

      // Compare trading days in IST
      const IST_OFFSET_MS = 5.5 * 3600 * 1000;
      const istNowMs = Date.now() + IST_OFFSET_MS;
      const todayIstDay = Math.floor(istNowMs / 86400000);
      const lastCandleIstDay = Math.floor((lastTs + IST_OFFSET_MS) / 86400000);

      // Find the most recent trading day (Mon-Fri):
      // Sat(6) → Fri, Sun(0) → Fri, otherwise today
      const istDate = new Date(istNowMs);
      const istDow = istDate.getUTCDay();
      const daysToLastTrading = istDow === 0 ? 2 : istDow === 6 ? 1 : 0;
      const lastTradingIstDay = todayIstDay - daysToLastTrading;
      const staleByDay = lastCandleIstDay < lastTradingIstDay;

      // Also catch an intraday hole: last candle is "today" but more than 10 minutes
      // old while the market is open (e.g. the backend cron missed a cycle). A
      // day-only check would never notice this since it's still the same trading day.
      const istMinutesOfDay = istDate.getUTCHours() * 60 + istDate.getUTCMinutes();
      const isWeekday = istDow >= 1 && istDow <= 5;
      const marketOpen = isWeekday && istMinutesOfDay >= 9 * 60 + 15 && istMinutesOfDay <= 15 * 60 + 30;
      const gapMinutes = (Date.now() - lastTs) / 60000;
      const staleByGap = marketOpen && lastCandleIstDay === todayIstDay && gapMinutes > 10;

      // No fill needed if last candle is fresh enough
      if (!staleByDay && !staleByGap) return false;

      const reason = staleByGap
        ? `${Math.round(gapMinutes)} min since last candle`
        : `${lastTradingIstDay - lastCandleIstDay} trading day(s)`;
      console.log(`[MarketPulse] ${symbol}: missing ${reason} — filling intraday from Kite`);
      setFetchProgress(p => ({ ...p, [symbol]: 'Filling gap…' }));
      setCandleStatus(p => ({ ...p, [symbol]: 'fetching' }));

      // Fetch 5m only — cascade aggregation on the backend builds 15m/1h/1d automatically
      const params = new URLSearchParams({ symbol, exchange: EXCHANGE_BY_SYMBOL[symbol] ?? 'NSE', timeframe: '5m' });
      await post(`/api/dashboard/prices/fetch-historical?${params.toString()}`, {}, { showLoading: false })
        .toPromise()
        .catch((e: any) => console.warn(`[MarketPulse] Gap fill 5m for ${symbol} failed:`, e));

      setFetchProgress(p => ({ ...p, [symbol]: '' }));
      setCandleStatus(p => ({ ...p, [symbol]: 'done' }));
      setChartKeys(p => ({ ...p, [symbol]: p[symbol] + 1 }));
      return true;
    } catch (e) {
      console.warn(`[MarketPulse] Gap fill failed for ${symbol}:`, e);
      setCandleStatus(p => ({ ...p, [symbol]: 'ok' })); // don't block UI on failure
      return false;
    }
  }, []);

  // ── Load option chain + compute levels ──
  const loadOptionChain = useCallback(async (symbol: string) => {
    try {
      const res: any = await get(OPTIONS_URLS.OPTION_CHAIN(symbol), { showLoading: false }).toPromise();
      if (!res?.data) return;
      const chain: OptionRow[] = res.data.option_chain || [];
      const pcr: number = res.data.pcr || 0;
      const maxPain = computeMaxPain(chain);
      const oiLevels = computeOILevels(chain, res.data.spot_price || 0);

      setIndexStates(prev => {
        const cur = prev[symbol];
        const lastPcr = cur.pcrTrend[cur.pcrTrend.length - 1]?.pcr || 0;
        const pcrTrend = Math.abs(pcr - lastPcr) > 0.005
          ? [...cur.pcrTrend, { time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }), pcr }].slice(-30)
          : cur.pcrTrend;
        return { ...prev, [symbol]: { ...cur, optionChain: chain, pcr, maxPain, oiLevels, pcrTrend, lastUpdated: new Date() } };
      });
    } catch (e) {
      console.warn(`[MarketPulse] Option chain unavailable for ${symbol}`);
    }
  }, []);

  // ── Load OI buildup/unwinding (change since last snapshot) ──
  // Backend captures OI snapshots every 5 min via a server-side cron (independent of
  // this tab); this reads the delta between the last two snapshots so the UI can show
  // fresh positioning (buildup) vs stale interest (unwinding), not just static OI levels.
  const loadOIChange = useCallback(async (symbol: string) => {
    try {
      const res: any = await get(OPTIONS_URLS.OI_CHANGE(symbol), { showLoading: false }).toPromise();
      const strikes: any[] = res?.data?.strikes ?? [];
      const oiChangeData: OIChangeRow[] = strikes.map((s: any) => ({
        strike: s.strike,
        callOIChange: s.callOIChange ?? 0,
        putOIChange: s.putOIChange ?? 0,
      }));
      const oiChangeTotals = strikes.length > 0
        ? { callChange: res.data.total_call_oi_change ?? 0, putChange: res.data.total_put_oi_change ?? 0 }
        : null;
      updateIndex(symbol, { oiChangeData, oiChangeTotals });
    } catch (e) {
      console.warn(`[MarketPulse] OI change unavailable for ${symbol}`);
    }
  }, [updateIndex]);

  // ── Load tech indicators + S/R levels ──
  const loadTechIndicators = useCallback(async (symbol: string) => {
    try {
      const res: any = await get(OPTIONS_URLS.TECHNICAL_INDICATORS(symbol), { showLoading: false }).toPromise();
      if (!res?.data) return;
      const { support, resistance } = computeTechLevels(res.data);
      updateIndex(symbol, { techIndicators: res.data, supportLevels: support, resistanceLevels: resistance });
    } catch (e) {
      console.warn(`[MarketPulse] Tech indicators unavailable for ${symbol}`);
    }
  }, [updateIndex]);

  // ── Load spot data ──
  const loadSpotData = useCallback(async (symbol: string) => {
    try {
      const res: any = await get(OPTIONS_URLS.SPOT_PRICE(symbol), { showLoading: false }).toPromise();
      if (!res?.data) return;
      updateIndex(symbol, { spotData: res.data, livePrice: res.data.spot_price });
    } catch (e) {
      console.warn(`[MarketPulse] Spot unavailable for ${symbol}`);
    }
  }, [updateIndex]);

  // ── Full refresh — loads live data only, no candle management ──
  const refreshAll = useCallback(async () => {
    setGlobalLoading(true);
    await Promise.all([
      ...INDEX_CONFIGS.flatMap(({ symbol }) => [
        loadSpotData(symbol),
        loadOptionChain(symbol),
        loadOIChange(symbol),
        loadTechIndicators(symbol),
      ]),
      loadVix(),
    ]);
    setLastRefreshed(new Date());
    setNextRefreshIn(300);
    setGlobalLoading(false);
  }, [loadSpotData, loadOptionChain, loadOIChange, loadTechIndicators, loadVix]);

  // ── On mount: fire data load + candle checks in parallel ──
  useEffect(() => {
    // Load spot/chain/indicators immediately — don't block on candle checks
    refreshAll();

    // Candle freshness check runs independently for both symbols in parallel
    Promise.all(INDEX_CONFIGS.map(async ({ symbol }) => {
      const fetched = (await checkAndFetchCandles(symbol)) || (await fillRecentGap(symbol));
      if (fetched) loadTechIndicators(symbol); // refresh indicators with new candle data
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 5-min countdown auto-refresh ──
  useEffect(() => {
    const tick = () => {
      setNextRefreshIn(prev => {
        if (prev <= 1) {
          // When WS is live, spot data comes from ticks — only refresh option chain.
          // Calling loadSpotData during live streaming resets livePrice/high/low with
          // a stale REST snapshot, which can corrupt the current candle open if it
          // fires right at a candle boundary.
          const tasks = wsEnabledRef.current
            ? INDEX_CONFIGS.flatMap(({ symbol }) => [loadOptionChain(symbol), loadOIChange(symbol)])
            : INDEX_CONFIGS.flatMap(({ symbol }) => [loadSpotData(symbol), loadOptionChain(symbol), loadOIChange(symbol)]);
          Promise.all(tasks);
          setLastRefreshed(new Date());
          return 300;
        }
        return prev - 1;
      });
    };
    countdownRef.current = setInterval(tick, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [loadSpotData, loadOptionChain, loadOIChange]);

  // Latest LTP per symbol since the last flush to the backend — lets the DB stay
  // current in real-time instead of live data only ever living in this tab's state
  const liveTickBufferRef = useRef<Map<string, number>>(new Map());

  // ── WebSocket tick handler ──
  const handleTick = useCallback((ticks: Map<number, TickData>) => {
    ticks.forEach((tick, token) => {
      const symbol = spotTokenMapRef.current.get(token);
      if (!symbol || !tick.lastPrice) return;
      liveTickBufferRef.current.set(symbol, tick.lastPrice);
      setIndexStates(prev => {
        const cur = prev[symbol];
        const sd = cur.spotData;
        const updated: Partial<IndexState> = { livePrice: tick.lastPrice };
        if (sd) {
          updated.spotData = {
            ...sd,
            spot_price: tick.lastPrice,
            high: Math.max(sd.high || 0, tick.lastPrice),
            low: (sd.low > 0) ? Math.min(sd.low, tick.lastPrice) : tick.lastPrice,
            open: sd.open || tick.open || tick.lastPrice,
            change: tick.lastPrice - sd.close,
            change_percent: sd.close > 0 ? ((tick.lastPrice - sd.close) / sd.close) * 100 : 0,
          };
        }
        return { ...prev, [symbol]: { ...cur, ...updated } };
      });
    });
  }, []);

  // ── Register WS callbacks ──
  useEffect(() => {
    const u1 = kiteWebSocket.onTick(handleTick);
    const u2 = kiteWebSocket.onStatus((s, m) => { setWsStatus(s); if (m) setWsMessage(m); });
    return () => { u1(); u2(); };
  }, [handleTick]);

  // ── Flush buffered live ticks to the backend every 10s while live, so the DB's
  // current 5m candle stays in sync with what's on screen instead of only living
  // in this tab's memory (this is what let a second tab see a stale gap before) ──
  useEffect(() => {
    if (!wsEnabled) return;
    const flush = () => {
      const buffered = liveTickBufferRef.current;
      if (buffered.size === 0) return;
      const ticks = Array.from(buffered.entries()).map(([symbol, ltp]) => ({ symbol, ltp }));
      buffered.clear();
      post('/api/dashboard/prices/live-tick', { ticks }, { showLoading: false })
        .toPromise()
        .catch((e: any) => console.warn('[MarketPulse] live-tick push failed:', e));
    };
    const intervalId = setInterval(flush, 10000);
    return () => clearInterval(intervalId);
  }, [wsEnabled]);

  // ── Connect WebSocket ──
  const connectWebSocket = async () => {
    try {
      setWsStatus('connecting');
      setWsMessage('Fetching credentials…');
      const credRes: any = await get(OPTIONS_URLS.WEBSOCKET_CREDENTIALS, { showLoading: false }).toPromise();
      if (!credRes?.data) throw new Error('No credentials');
      const { api_key, access_token } = credRes.data;

      const tokenMap = new Map<number, string>();
      const allInstruments: InstrumentToken[] = [];
      const tokensToSub: number[] = [];

      for (const { symbol } of INDEX_CONFIGS) {
        try {
          const tokRes: any = await get(OPTIONS_URLS.INSTRUMENT_TOKENS(symbol), { showLoading: false }).toPromise();
          const instruments: InstrumentToken[] = tokRes?.data?.tokens || [];
          const idx = instruments.find(i => i.type === 'INDEX');
          if (idx) { tokenMap.set(idx.token, symbol); tokensToSub.push(idx.token); allInstruments.push(idx); }
        } catch { /* token unavailable */ }
      }

      spotTokenMapRef.current = tokenMap;
      kiteWebSocket.initialize(api_key, access_token);
      kiteWebSocket.setTokenMap(allInstruments);
      await kiteWebSocket.connect();
      kiteWebSocket.subscribe(tokensToSub, 'quote');
      setWsEnabled(true);
      setWsMessage('Live streaming');
    } catch (err: any) {
      setWsStatus('error');
      setWsMessage(err.message || 'Connection failed');
    }
  };

  const disconnectWebSocket = () => {
    kiteWebSocket.disconnect();
    setWsEnabled(false);
    setWsStatus('disconnected');
    setWsMessage('');
    spotTokenMapRef.current.clear();
  };

  // ── Cleanup ──
  useEffect(() => {
    return () => {
      if (wsEnabled) kiteWebSocket.disconnect();
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [wsEnabled]);

  // ─── Render ───────────────────────────────────────────────────────────────

  const wsStatusColor = wsStatus === 'connected' ? '#26a69a' : wsStatus === 'connecting' ? '#ffd54f' : wsStatus === 'error' ? '#ef5350' : '#555';
  const fmtCountdown = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div style={{ background: '#0a0a12', minHeight: '100vh', padding: '0.5rem', fontFamily: "'JetBrains Mono', monospace, sans-serif" }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.4rem 0.5rem', marginBottom: '0.5rem', background: '#131722', borderRadius: 6, border: '1px solid #1e222d', flexWrap: 'wrap' }}>
        <span style={{ color: '#d1d4dc', fontWeight: 800, fontSize: '0.85rem', letterSpacing: 1 }}>MARKET PULSE</span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: wsStatusColor, boxShadow: wsStatus === 'connected' ? `0 0 6px ${wsStatusColor}` : 'none' }} />
          <span style={{ fontSize: '0.65rem', color: wsStatusColor }}>{wsMessage || wsStatus}</span>
        </div>

        {wsEnabled ? (
          <button onClick={disconnectWebSocket} style={{ padding: '3px 10px', fontSize: '0.65rem', background: '#1a2e1a', border: '1px solid #26a69a', color: '#26a69a', borderRadius: 3, cursor: 'pointer' }}>
            Disconnect Live
          </button>
        ) : (
          <button onClick={connectWebSocket} disabled={wsStatus === 'connecting'} style={{ padding: '3px 10px', fontSize: '0.65rem', background: '#1e222d', border: '1px solid #2962ff', color: '#2962ff', borderRadius: 3, cursor: 'pointer', opacity: wsStatus === 'connecting' ? 0.5 : 1 }}>
            {wsStatus === 'connecting' ? 'Connecting…' : 'Go Live'}
          </button>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {vixData && (
            <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', background: '#1e222d', borderRadius: 4, padding: '2px 8px' }}>
              <span style={{ fontSize: '0.48rem', color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>India VIX</span>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: vixData.value > 20 ? '#ef5350' : vixData.value < 12 ? '#26a69a' : '#ffd54f', fontFamily: 'monospace' }}>
                {vixData.value.toFixed(2)}
              </span>
            </div>
          )}
          {!wsEnabled && (
            <span style={{ fontSize: '0.62rem', color: '#555' }}>
              Refresh in <span style={{ color: '#d1d4dc' }}>{fmtCountdown(nextRefreshIn)}</span>
            </span>
          )}
          {lastRefreshed && (
            <span style={{ fontSize: '0.62rem', color: '#555' }}>
              Updated: <span style={{ color: '#787b86' }}>{lastRefreshed.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
            </span>
          )}
          <button onClick={refreshAll} disabled={globalLoading} style={{ padding: '3px 10px', fontSize: '0.65rem', background: '#1e222d', border: '1px solid #363a45', color: globalLoading ? '#555' : '#d1d4dc', borderRadius: 3, cursor: globalLoading ? 'not-allowed' : 'pointer' }}>
            {globalLoading ? 'Loading…' : '↻ Refresh'}
          </button>
          <span style={{ fontSize: '0.6rem' }}>
            {isMarketOpen() ? <span style={{ color: '#26a69a' }}>● Market Open</span> : <span style={{ color: '#555' }}>● Market Closed</span>}
          </span>
        </div>
      </div>

      {/* Session Phase + Expiry Bar */}
      <SessionExpiryBar />

      {/* Level Legend */}
      <div style={{ display: 'flex', gap: '0.75rem', padding: '0.25rem 0.5rem', marginBottom: '0.4rem', fontSize: '0.6rem', color: '#555', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ color: '#787b86' }}>Levels:</span>
        <span><span style={{ color: '#ef5350' }}>●</span> CE Wall (Resistance)</span>
        <span><span style={{ color: '#26a69a' }}>●</span> PE Wall (Support)</span>
        <span><span style={{ color: '#ffd54f' }}>●</span> Max Pain</span>
        <span><span style={{ color: '#4fc3f7' }}>—</span> Pivots</span>
        <span><span style={{ color: '#FF9800' }}>—</span> MAs</span>
        <span><span style={{ color: '#9c27b0' }}>—</span> BB</span>
        <span style={{ marginLeft: 'auto', color: '#333', fontSize: '0.55rem' }}>No AI · Pure data</span>
      </div>

      {/* Dual panel */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
        {INDEX_CONFIGS.map(cfg => (
          <IndexPanel
            key={cfg.symbol}
            config={cfg}
            state={indexStates[cfg.symbol]}
            wsEnabled={wsEnabled}
            candleStatus={candleStatus[cfg.symbol]}
            fetchProgress={fetchProgress[cfg.symbol]}
            chartKey={chartKeys[cfg.symbol]}
            onManualFetch={() => checkAndFetchCandles(cfg.symbol)}
            onShowLevels={() => setLevelsModalSymbol(cfg.symbol)}
            signalMarkers={signalMarkers[cfg.symbol] ?? []}
            onSignalChange={(sig) => handleSignalChange(cfg.symbol, sig)}
            chartPatterns={chartPatterns[cfg.symbol] ?? []}
            onPatternsDetected={(patterns) => handlePatternsDetected(cfg.symbol, patterns)}
          />
        ))}
      </div>

      {/* Levels Modal */}
      {levelsModalSymbol && (() => {
        const cfg = INDEX_CONFIGS.find(c => c.symbol === levelsModalSymbol)!;
        return (
          <LevelsModal
            config={cfg}
            state={indexStates[levelsModalSymbol]}
            onClose={() => setLevelsModalSymbol(null)}
          />
        );
      })()}

      {/* Bottom info */}
      <div style={{ marginTop: '0.5rem', padding: '0.35rem 0.75rem', background: '#131722', border: '1px solid #1e222d', borderRadius: 6, display: 'flex', gap: '1.5rem', fontSize: '0.62rem', color: '#555', flexWrap: 'wrap' }}>
        {INDEX_CONFIGS.map(({ symbol, label, color }) => {
          const s = indexStates[symbol];
          const cs = candleStatus[symbol];
          return (
            <span key={symbol}>
              <span style={{ color }}>{label}</span>
              {' · '}PCR <span style={{ color: s.pcr > 1.2 ? '#26a69a' : s.pcr < 0.7 ? '#ef5350' : '#ffd54f' }}>{s.pcr ? s.pcr.toFixed(2) : '—'}</span>
              {' · '}MaxPain <span style={{ color: '#9e9e9e' }}>{s.maxPain ? s.maxPain.toLocaleString('en-IN') : '—'}</span>
              {' · '}Candles <span style={{ color: cs === 'ok' || cs === 'done' ? '#26a69a' : cs === 'fetching' ? '#ffd54f' : '#555' }}>{cs}</span>
            </span>
          );
        })}
        <span style={{ marginLeft: 'auto', color: '#333' }}>OI levels auto-computed · No AI/Grok</span>
      </div>
    </div>
  );
};

export default MarketPulseDashboard;
